import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarRange, Loader2 } from "lucide-react";
import ExportButton from "@/components/ExportButton";

const money = (v) => `$${(Number(v) || 0).toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const uds = (v) => (Number(v) || 0).toLocaleString("es-EC", { maximumFractionDigits: 2 });
const hoy = () => new Date().toISOString().slice(0, 10);

function Resumen({ t, rango }) {
  const items = rango
    ? [["Inventario inicial", t.inicial_valor, t.inicial_uds, "ini"], ["Entradas", t.entradas_valor, t.entradas_uds, "ent"], ["Salidas", t.salidas_valor, t.salidas_uds, "sal"], ["Inventario final", t.final_valor, t.final_uds, "fin"]]
    : [["Valor del inventario", t.final_valor, t.final_uds, "fin"]];
  return (
    <div className={`grid gap-3 ${rango ? "grid-cols-2 md:grid-cols-4" : "grid-cols-1 md:grid-cols-3"}`}>
      {items.map(([label, valor, cant, k]) => (
        <div key={k} className={`rounded-md border p-3 ${k === "fin" ? "border-[#205c45] bg-[#e9f2ee]" : "border-[#d1dbd6] bg-white"}`} data-testid={`valoriz-total-${k}`}>
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#5c7368]">{label}</div>
          <div className="text-xl font-bold tabular-nums text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>{k === "sal" ? "−" : ""}{money(valor)}</div>
          <div className="text-xs text-[#4a5c53] tabular-nums">{uds(cant)} uds</div>
        </div>
      ))}
    </div>
  );
}

const COLS_RANGO = ["inicial", "entradas", "salidas", "final"];

export function ValorizacionInventario({ bodegaId, laboratorioId }) {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState(hoy());
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const calcular = async () => {
    if (!hasta) { toast.error("Escoja la fecha de corte"); return; }
    setLoading(true);
    try {
      const params = { hasta };
      if (desde) params.desde = desde;
      if (bodegaId !== "all") params.bodega_id = bodegaId;
      if (laboratorioId !== "all") params.laboratorio_id = laboratorioId;
      const { data } = await api.get("/inventory-report/valorizacion", { params });
      setRes(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const rango = res?.modo === "rango";
  const grupos = rango ? COLS_RANGO : ["final"];
  const exportCols = [{ label: "Código", accessor: "codigo_interno" }, { label: "Producto", accessor: "nombre_comercial" }, { label: "Laboratorio", accessor: "laboratorio" },
    ...grupos.flatMap(g => [{ label: `${g} uds`, accessor: `${g}_uds` }, { label: `${g} valor`, accessor: `${g}_valor` }])];

  return (
    <div className="rounded-md border border-[#d1dbd6] bg-white p-4 space-y-4" data-testid="valorizacion-inventario">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#5c7368]"><CalendarRange size={14} />Valor del inventario a una fecha</div>
          <p className="text-xs text-[#4a5c53] mt-1 max-w-xl">Solo fecha de corte: valor en ese día. Con fecha inicial: inventario inicial, entradas, salidas y final del rango. Se valoriza al costo de compra de cada lote. Usa los filtros de bodega y laboratorio de arriba.</p>
        </div>
        <div><Label className="text-[11px]">Desde (opcional)</Label><Input type="date" value={desde} max={hasta} onChange={(e) => setDesde(e.target.value)} data-testid="valoriz-desde" /></div>
        <div><Label className="text-[11px]">Fecha de corte</Label><Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="valoriz-hasta" /></div>
        <Button onClick={calcular} className="bg-[#205c45]" disabled={loading} data-testid="valoriz-calcular">
          {loading ? <><Loader2 size={14} className="mr-1 animate-spin" />Calculando…</> : "Calcular valor"}
        </Button>
        {res && <ExportButton title={`Valorización inventario ${res.desde ? `${res.desde} a ` : ""}${res.hasta}`} columns={exportCols} rows={res.rows} filename={`valorizacion-inventario-${res.hasta}`} />}
      </div>
      {res && (
        <>
          <Resumen t={res.totales} rango={rango} />
          <div className="overflow-auto max-h-[420px] rounded border border-[#d1dbd6]">
            <table className="erp-table">
              <thead><tr><th>Producto</th><th>Laboratorio</th>{grupos.map(g => <th key={g} className="text-right capitalize">{g}</th>)}</tr></thead>
              <tbody>
                {res.rows.map(r => (
                  <tr key={r.producto_comercial_id} data-testid={`valoriz-row-${r.producto_comercial_id}`}>
                    <td className="font-medium">{r.nombre_comercial}<div className="text-[10px] text-[#8ba397] font-mono">{r.codigo_interno} · {r.n_lotes} lote(s)</div></td>
                    <td className="text-xs">{r.laboratorio || "—"}</td>
                    {grupos.map(g => <td key={g} className="text-right tabular-nums"><div className="font-semibold">{money(r[`${g}_valor`])}</div><div className="text-[10px] text-[#5c7368]">{uds(r[`${g}_uds`])} uds</div></td>)}
                  </tr>
                ))}
                {res.rows.length === 0 && <tr><td colSpan={2 + grupos.length} className="text-center py-8 text-[#8ba397]">Sin inventario en esa fecha</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
